import { Box, Typography } from '@mui/material';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { Field } from 'src/API';
import { FIELD_TYPES, FieldInput } from 'src/api/process-module';
import { RootState } from 'src/store';
import {
  ActivityTableField,
  CheckboxListField,
  CommentField,
  DividerField,
  DropdownField,
  DynamicTableField,
  FileUploadField,
  HtmlField,
  ImageField,
  InfiniteCanvasField,
  LinkField,
  MarkdownField,
  ProcessModuleInfiniteCanvasField,
  ProcessModuleRootCanvasField,
  StaticFileField,
} from '../components';
import { VideoField } from '../components/video-field';

interface FieldTypeConfigProps {
  field: FieldInput & Partial<Field>;
  index: number;
  handleFieldChange: (index: number, field: Partial<FieldInput & Partial<Field>>) => void;
  isSubmitting: boolean;
  onFieldSelect: () => void;
  moduleId: string;
  allFormFields?: (FieldInput & Partial<Field>)[];
}

export const FieldTypeConfig = ({
  field,
  index,
  handleFieldChange,
  isSubmitting,
  onFieldSelect,
  moduleId,
  allFormFields,
}: FieldTypeConfigProps) => {
  const existingFields = useSelector((state: RootState) => state.processModule.fields);
  const [selectedFieldId, setSelectedFieldId] = useState<string>('');

  const handleExistingFieldSelect = () => {
    setSelectedFieldId('');
    onFieldSelect();
    const selectedField = existingFields.find((f) => f.id === '');
    if (selectedField) {
      handleFieldChange(index, {
        type: selectedField.type as any,
        dataSource: selectedField.dataSource,
        dataSourceType: selectedField.dataSourceType,
        name: selectedField.name,
        label: selectedField.label || undefined,
      });
    }
  };

  const isUsingExistingField = selectedFieldId !== '';

  return (
    <Box sx={{ width: '100%' }}>
      {(() => {
        switch (field.type) {
          case FIELD_TYPES.ACTIVITY_TABLE:
            return (
              <ActivityTableField
                field={field}
                index={index}
                handleFieldChange={handleFieldChange}
                isSubmitting={isSubmitting}
                isUsingExistingField={isUsingExistingField}
              />
            );

          case FIELD_TYPES.CHECKBOX_LIST:
            return (
              <CheckboxListField
                field={field}
                index={index}
                handleFieldChange={handleFieldChange}
                isSubmitting={isSubmitting}
                isUsingExistingField={isUsingExistingField}
              />
            );

          case FIELD_TYPES.COMMENT:
            return (
              <CommentField
                field={field}
                index={index}
                handleFieldChange={handleFieldChange}
                isSubmitting={isSubmitting}
                isUsingExistingField={isUsingExistingField}
              />
            );

          case FIELD_TYPES.DIVIDER:
            return (
              <DividerField
                field={field}
                index={index}
                handleFieldChange={handleFieldChange}
                isSubmitting={isSubmitting}
                isUsingExistingField={isUsingExistingField}
              />
            );

          case FIELD_TYPES.DROPDOWN:
            return (
              <DropdownField
                field={field}
                index={index}
                handleFieldChange={handleFieldChange}
                isSubmitting={isSubmitting}
                isUsingExistingField={isUsingExistingField}
              />
            );

          case FIELD_TYPES.DYNAMIC_TABLE:
            return (
              <DynamicTableField
                field={field}
                index={index}
                handleFieldChange={handleFieldChange}
                isSubmitting={isSubmitting}
                isUsingExistingField={isUsingExistingField}
              />
            );

          case FIELD_TYPES.FILE_UPLOAD:
            return (
              <FileUploadField
                field={field}
                index={index}
                handleFieldChange={handleFieldChange}
                isSubmitting={isSubmitting}
                isUsingExistingField={isUsingExistingField}
              />
            );

          case FIELD_TYPES.HTML:
            return (
              <HtmlField
                field={field}
                index={index}
                handleFieldChange={handleFieldChange}
                isSubmitting={isSubmitting}
                isUsingExistingField={isUsingExistingField}
              />
            );

          case FIELD_TYPES.IMAGE:
            return (
              <ImageField
                field={field}
                index={index}
                handleFieldChange={handleFieldChange}
                isSubmitting={isSubmitting}
                isUsingExistingField={isUsingExistingField}
              />
            );

          // case FIELD_TYPES.INFINITE_CANVAS:
          //   return (
          //     <InfiniteCanvasField
          //       field={field}
          //       index={index}
          //       handleFieldChange={handleFieldChange}
          //       isSubmitting={isSubmitting}
          //       isUsingExistingField={isUsingExistingField}
          //     />
          //   );

          case FIELD_TYPES.PROCESS_MODULE_INFINITE_CANVAS:
            return (
              <ProcessModuleInfiniteCanvasField
                field={field}
                index={index}
                handleFieldChange={handleFieldChange}
                isSubmitting={isSubmitting}
                isUsingExistingField={isUsingExistingField}
                moduleId={moduleId}
                onSelectExistingField={handleExistingFieldSelect}
                allFormFields={allFormFields}
              />
            );

          case FIELD_TYPES.PROCESS_MODULE_ROOT_CANVAS:
            return (
              <ProcessModuleRootCanvasField
                field={field}
                index={index}
                handleFieldChange={handleFieldChange}
                isSubmitting={isSubmitting}
                isUsingExistingField={isUsingExistingField}
                moduleId={moduleId}
                onSelectExistingField={handleExistingFieldSelect}
                allFormFields={allFormFields}
              />
            );

          case FIELD_TYPES.LINK:
            return (
              <LinkField
                field={field}
                index={index}
                handleFieldChange={handleFieldChange}
                isSubmitting={isSubmitting}
                isUsingExistingField={isUsingExistingField}
              />
            );

          case FIELD_TYPES.MARKDOWN:
            return (
              <MarkdownField
                field={field}
                index={index}
                handleFieldChange={handleFieldChange}
                isSubmitting={isSubmitting}
                isUsingExistingField={isUsingExistingField}
              />
            );

          case FIELD_TYPES.STATIC_FILE:
            return (
              <StaticFileField
                field={field}
                index={index}
                handleFieldChange={handleFieldChange}
                isSubmitting={isSubmitting}
                isUsingExistingField={isUsingExistingField}
              />
            );
          case FIELD_TYPES.VIDEO:
            return <VideoField field={field} index={index} handleFieldChange={handleFieldChange} />;

          case FIELD_TYPES.INNOVATION_READINESS_LEVELS:
            return <></>;

          default:
            return (
              <Typography variant="body2" color="error">
                Unknown field type: {field.type}
              </Typography>
            );
        }
      })()}

      {/* <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
        <FormControl fullWidth>
          <InputLabel>Use Existing Field</InputLabel>
          <Select
            value={selectedFieldId}
            label="Use Existing Field"
            onChange={(e) => handleExistingFieldSelect(e.target.value)}
            disabled={isSubmitting}
          >
            <MenuItem value="">
              <em>None</em>
            </MenuItem>
            {existingFields.map((f) => (
              <MenuItem key={f.id} value={f.id}>
                {f.name} ({f.type})
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack> */}
    </Box>
  );
};

export interface FieldTypeConfig {
  label: string;
  description: string;
  properties: {
    name: string;
    label: string;
    type: 'text' | 'json' | 'boolean' | 'select';
    required?: boolean;
    options?: { label: string; value: string }[];
    helperText?: string;
  }[];
}

export const FIELD_TYPE_CONFIGS: Record<string, FieldTypeConfig> = {
  [FIELD_TYPES.ACTIVITY_TABLE]: {
    label: 'Activity Table',
    description: 'A table for displaying activities',
    properties: [
      {
        name: 'tableConfig',
        label: 'Table Configuration',
        type: 'json',
        helperText: 'Enter table configuration in JSON format',
      },
    ],
  },
  [FIELD_TYPES.CHECKBOX_LIST]: {
    label: 'Checkbox List',
    description: 'A list of checkboxes',
    properties: [
      {
        name: 'options',
        label: 'Options',
        type: 'json',
        helperText:
          "Enter JSON array of options: [{'name': 'Option 1', 'label': 'Option 1'}, {'name': 'Option 2', 'label': 'Option 2'}]",
      },
    ],
  },
  [FIELD_TYPES.COMMENT]: {
    label: 'Comment',
    description: 'A text area for comments',
    properties: [
      {
        name: 'rows',
        label: 'Number of Rows',
        type: 'text',
        helperText: 'Enter number of rows (default: 4)',
      },
    ],
  },
  [FIELD_TYPES.DIVIDER]: {
    label: 'Divider',
    description: 'A divider',
    properties: [],
  },
  [FIELD_TYPES.DROPDOWN]: {
    label: 'Dropdown',
    description: 'A dropdown list of options',
    properties: [
      {
        name: 'options',
        label: 'Options',
        type: 'json',
        helperText: "Enter JSON array of options: [{'name': 'Option 1'}, {'name': 'Option 2'}]",
      },
    ],
  },
  [FIELD_TYPES.DYNAMIC_TABLE]: {
    label: 'Dynamic Table',
    description: 'A table with dynamic configuration',
    properties: [
      {
        name: 'tableConfig',
        label: 'Table Configuration',
        type: 'json',
        helperText: 'Enter dynamic table configuration in JSON format',
      },
    ],
  },
  [FIELD_TYPES.FILE_UPLOAD]: {
    label: 'File Upload',
    description: 'A field for uploading files',
    properties: [
      {
        name: 'acceptedTypes',
        label: 'Accepted File Types',
        type: 'text',
        helperText: 'Enter comma-separated list of accepted file types (e.g., .pdf,.doc,.docx)',
      },
    ],
  },
  [FIELD_TYPES.HTML]: {
    label: 'HTML',
    description: 'HTML content',
    properties: [
      {
        name: 'content',
        label: 'HTML Content',
        type: 'text',
        helperText: 'Enter HTML content',
      },
    ],
  },
  [FIELD_TYPES.IMAGE]: {
    label: 'Image',
    description: 'An image field',
    properties: [
      {
        name: 'src',
        label: 'Image URL',
        type: 'text',
        helperText: 'Enter image URL',
      },
    ],
  },
  // [FIELD_TYPES.INFINITE_CANVAS]: {
  //   label: 'Infinite Canvas',
  //   description: 'A button that links to an infinite canvas',
  //   properties: [
  //     {
  //       name: 'override',
  //       label: 'Button Label',
  //       type: 'text',
  //       helperText: 'Enter the label for the button',
  //     },
  //   ],
  // },
  [FIELD_TYPES.PROCESS_MODULE_INFINITE_CANVAS]: {
    label: 'Process Module Infinite Canvas',
    description: 'A field that links to an infinite canvas from a process module',
    properties: [
      {
        name: 'canvasId',
        label: 'Canvas ID',
        type: 'text',
        helperText: 'The ID of the canvas to link to',
      },
    ],
  },
  [FIELD_TYPES.PROCESS_MODULE_ROOT_CANVAS]: {
    label: 'Canvas Field',
    description: 'A canvas field that can create a canvas once or reference another canvas field',
    properties: [
      {
        name: 'canvasId',
        label: 'Canvas ID',
        type: 'text',
        helperText: 'The ID of the canvas to link to',
      },
      {
        name: 'isRootField',
        label: 'Is Root Field',
        type: 'boolean',
        helperText: 'Whether this field can create a canvas (true) or references another field (false)',
      },
    ],
  },
  [FIELD_TYPES.INNOVATION_READINESS_LEVELS]: {
    label: 'Innovation Readiness Levels',
    description: 'A field for innovation readiness levels',
    properties: [
      {
        name: 'levelsConfig',
        label: 'Levels Configuration',
        type: 'json',
        helperText: 'Enter innovation readiness levels configuration in JSON format',
      },
    ],
  },
  [FIELD_TYPES.LINK]: {
    label: 'Link',
    description: 'A URL input field',
    properties: [],
  },
  [FIELD_TYPES.MARKDOWN]: {
    label: 'Markdown',
    description: 'Markdown content with live preview',
    properties: [
      {
        name: 'content',
        label: 'Markdown Content',
        type: 'text',
        helperText: 'Enter markdown content',
      },
    ],
  },
  [FIELD_TYPES.STATIC_FILE]: {
    label: 'Static File',
    description: 'A field for static file reference',
    properties: [
      {
        name: 'fileUrl',
        label: 'File URL',
        type: 'text',
        helperText: 'Enter the URL of the static file',
      },
    ],
  },
};
